/**
 * Desplazamiento horizontal con la rueda del ratón en contenedores `.custom-scrollbar` (tablas
 * anchas, AG Grid): si el contenedor solo desborda en horizontal (o se pulsa Shift), la rueda lo
 * mueve a lo ancho; al llegar al borde, el scroll pasa al padre (scroll anidado).
 *
 * Rendimiento: NO hay un listener `wheel` no pasivo en `document` (bloquearía el scroll de toda la
 * página esperando a JS). Un `mouseover` pasivo y delegado detecta el contenedor bajo el puntero y
 * le engancha, una sola vez, su propio listener `wheel` (no pasivo solo ahí, porque necesita
 * `preventDefault`). La raíz (`<html class="custom-scrollbar">`) y `<body>` se excluyen.
 */
const wired = new WeakSet<HTMLElement>();

function scrollTargetOf(container: HTMLElement): HTMLElement {
  if (container.classList.contains('ag-theme-grid') || container.classList.contains('ag-theme-quartz')) {
    return container.querySelector<HTMLElement>('.ag-center-cols-viewport') ?? container;
  }
  return container;
}

function onWheel(this: HTMLElement, e: WheelEvent): void {
  const el = scrollTargetOf(this);
  const hasHorizontal = el.scrollWidth > el.clientWidth;
  const hasVertical = el.scrollHeight > el.clientHeight;
  if (!hasHorizontal || (hasVertical && !e.shiftKey)) return;

  const atLeft = el.scrollLeft <= 0;
  const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
  // En el borde y empujando hacia fuera: se deja que el padre haga scroll vertical.
  if ((atLeft && e.deltaY < 0) || (atRight && e.deltaY > 0)) return;

  e.preventDefault();
  el.scrollLeft += e.deltaY;
}

function onPointerOver(e: MouseEvent): void {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const container = target.closest<HTMLElement>('.custom-scrollbar');
  if (!container || container === document.documentElement || container === document.body) return;
  if (wired.has(container)) return;
  wired.add(container);
  container.addEventListener('wheel', onWheel, { passive: false });
}

export function installHorizontalWheelScroll(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('mouseover', onPointerOver, { passive: true });
}
